import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import {
  amountField,
  booleanField,
  sharesField,
  simpleMultilineTextField,
  simpleTextField,
} from '../../../data-fields/CommonFields';
import { RendererType } from '../../../data-models/field-definition';
import { ICurrencyMeta, INumberMeta } from '../../../data-models/field3.data-model';
import { fundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { companyMetricsAtomJ } from '../../../services/state/CompanyMetricsStateJ';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { FMT } from '../../../util/formatter-service';
import { createFormField, IFormField } from '../../../view-models/form.view-model';
import {
  getRestructureIdsMeta,
  ITransactionViewModel,
  ITransactionViewModelInternal,
  restructureKeyToFieldKey,
} from '../../../view-models/transaction-form.view-model';
import { useFundDateCurrencyFields } from './useFundDateCurrencyFields';

export function useShareExchangeFields(
  companyId: number,
  selectedFundId: number | null,
  transactionDate: string
) {
  const { showStockSplitInShareExchange } = useFlags<MaggieFeatureFlags>();
  const getCommonInitialFields = useFundDateCurrencyFields();
  const getRestructureIdFields = useGetRestructureIdFields(companyId);

  return useCallback(() => {
    const fields: IFormField<unknown>[] = [...getCommonInitialFields()];

    if (showStockSplitInShareExchange) {
      fields.push(
        booleanField({ key: '_viewModel.useSplitByRestructureId', label: 'Stock Split', required: true })
      );
    }
    const restructureFields = getRestructureIdFields(selectedFundId, transactionDate);

    return [...fields, ...restructureFields, simpleMultilineTextField({ key: 'notes', label: 'Notes' })];
  }, [
    getCommonInitialFields,
    getRestructureIdFields,
    selectedFundId,
    showStockSplitInShareExchange,
    transactionDate,
  ]);
}

export function useGetRestructureIdFields(companyId: number) {
  const useSplitByRestructureId = useWatch<ITransactionViewModel>({
    name: '_viewModel.useSplitByRestructureId',
  }) as ITransactionViewModelInternal['useSplitByRestructureId'];
  const fundsMap = useAtomValue(fundsByIdMapAtom);
  // The RQ cache entry is observer-less while the form is open and evicted after
  // gcTime, so the atom's value outlasts it.
  const metrics = useAtomValue(companyMetricsAtomJ({ companyId }));

  return useCallback(
    (selectedFundId: number | null, transactionDate: string) => {
      const latestRestructureTransactions = getRestructureIdsMeta(
        metrics?.metrics[0]?.transactions ?? [],
        selectedFundId,
        new Date(transactionDate)
      );

      const fields: IFormField<unknown>[] = [];

      if (!useSplitByRestructureId) {
        fields.push(sharesField({ key: 'receivedSharesNo', label: 'Received Shares', required: true }));
        fields.push(amountField({ key: 'receivedPricePerShare', label: 'Received PPS', required: true }));
        fields.push(simpleTextField({ key: 'security', label: 'Received Security' }));
      }

      Array.from(latestRestructureTransactions.values()).forEach((transaction) => {
        const { fundId, position, restructureId, transactionDate } = transaction;
        const fundLabel = fundId && fundsMap.get(fundId)?.name ? `Fund: ${fundsMap.get(fundId)?.name}, ` : '';
        const description = `${fundLabel}Acquired Date: ${FMT.format('dateNumeric', transactionDate)}`;

        const exchangedShares = createFormField<ICurrencyMeta>({
          key: `_viewModel.exchangedSharesByRestructureId.${restructureKeyToFieldKey(restructureId ?? '')}`,
          label: `Exchanged shares for ${position ?? FMT.format('dateNumeric', transactionDate)}`,
          description,
          required: true,
          renderer: RendererType.number,
        });
        fields.push(exchangedShares);

        if (useSplitByRestructureId) {
          const receivedShares = createFormField<INumberMeta>({
            key: `_viewModel.receivedSharesByRestructureId.${restructureKeyToFieldKey(restructureId ?? '')}`,
            label: `Received shares for ${position ?? FMT.format('dateNumeric', transactionDate)}`,
            description,
            required: true,
            renderer: RendererType.number,
          });

          const receivedPps = createFormField<ICurrencyMeta>({
            key: `_viewModel.receivedPpsByRestructureId.${restructureKeyToFieldKey(restructureId ?? '')}`,
            label: `Received PPS for ${position ?? FMT.format('dateNumeric', transactionDate)}`,
            description,
            required: true,
            renderer: RendererType.number,
          });

          const receivedSecurity = simpleTextField({
            key: `_viewModel.receivedSecurityByRestructureId.${restructureKeyToFieldKey(restructureId ?? '')}`,
            label: `Received Security for ${position ?? FMT.format('dateNumeric', transactionDate)}`,
          });

          fields.push(receivedShares);
          fields.push(receivedPps);
          fields.push(receivedSecurity);
        }
      });

      return fields;
    },
    [fundsMap, metrics, useSplitByRestructureId]
  );
}
